import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import SumSum from './SumSum';
import { LoginDialog } from './Login/Login';
import { EmailVerificationOverlay } from './Login/EmailVerificationOverlay';
import { EmailVerified } from './Login/EmailVerified';
import { BottomMenu } from './components/BottomMenu';
import { TemplateDialog } from './components/TemplateDialog';
import { OpenDocumentsDialog } from './components/OpenDocumentsDialog';
import { ShareDialog } from './components/ShareDialog';
import { SharedDocumentViewer } from './components/SharedDocumentViewer';
import { Toaster } from 'react-hot-toast';

const HomePage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loginOpen, setLoginOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const sumSumRef = useRef(null);

  // Check if we should open the login modal based on URL parameter
  useEffect(() => {
    if (searchParams.get('login') === 'true') {
      setLoginOpen(true);
      // Remove the parameter from URL
      searchParams.delete('login');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleNewDocument = () => {
    if (sumSumRef.current) {
      sumSumRef.current.resetDocument();
    }
  };

  const handleSelectTemplate = (template) => {
    if (sumSumRef.current) {
      sumSumRef.current.loadTemplate(template);
    }
  };

  const handleSelectDocument = (document) => {
    if (sumSumRef.current) {
      sumSumRef.current.loadDocument(document);
    }
  };

  const handleShare = () => {
    setShareOpen(true);
  };

  // Set up global share trigger for SumSum component
  useEffect(() => {
    window.triggerShare = handleShare;
    return () => {
      delete window.triggerShare;
    };
  }, []);

  const handleDocumentDeleted = (deletedDocId, remainingDocs) => {
    // Check if the deleted document is currently open
    const currentDocId = sumSumRef.current?.getCurrentDocumentId?.();

    if (currentDocId === deletedDocId) {
      // The current document was deleted
      if (remainingDocs.length > 0) {
        // Load the first remaining document
        sumSumRef.current?.loadDocument(remainingDocs[0]);
      } else {
        // No documents left, reset to new document
        sumSumRef.current?.resetDocument();
      }
    }
  };

  // Get current document data for sharing
  const getDocumentData = () => {
    if (sumSumRef.current) {
      return sumSumRef.current.getDocumentData?.() || { title: '', blocks: [] };
    }
    return { title: '', blocks: [] };
  };

  // Check if user needs email verification
  const needsEmailVerification = user &&
    user.providerData[0]?.providerId === 'password' &&
    !user.emailVerified;

  return (
    <>
      <SumSum ref={sumSumRef} />

      <BottomMenu
        onOpenClick={() => setDocumentsOpen(true)}
        onLoginClick={() => setLoginOpen(true)}
        onTemplateClick={() => setTemplateOpen(true)}
        onNewClick={handleNewDocument}
      />

      <LoginDialog isMenuOpen={loginOpen} setMenuOpen={setLoginOpen} />
      <TemplateDialog
        isOpen={templateOpen}
        onClose={() => setTemplateOpen(false)}
        onSelectTemplate={handleSelectTemplate}
      />
      <OpenDocumentsDialog
        isOpen={documentsOpen}
        onClose={() => setDocumentsOpen(false)}
        onSelectDocument={handleSelectDocument}
        onDocumentDeleted={handleDocumentDeleted}
        currentDocumentId={sumSumRef.current?.getCurrentDocumentId?.()}
      />
      <ShareDialog
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        title={getDocumentData().title}
        blocks={getDocumentData().blocks}
        userId={user?.uid}
      />

      {needsEmailVerification && <EmailVerificationOverlay user={user} />}
    </>
  );
};

function App() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0a0a0a]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/email-verified" element={<EmailVerified />} />
        <Route path="/view/:shareId" element={<SharedDocumentViewer />} />
      </Routes>
      <Toaster position="top-center" />
    </BrowserRouter>
  );
}

export default App;
